import { useNavigate, useParams } from 'react-router';
import { useIntl } from 'react-intl';
import routes from '@commons/routes';
import { notifications } from '@mantine/notifications';
import {
  Center,
  Container,
  Flex,
  Loader,
  NativeSelect,
  Textarea,
  Title,
} from '@mantine/core';

import { Button, TextInput } from '@mantine/core';
import { ScriptTagsWarning } from '@components/ScriptTagsWarning';
import { TemplateInfoAlert } from '@components/TemplateInfoAlert';
import { CodeEditor } from '@components/CodeEditor';
import { useForm } from '@mantine/form';
import { FormBuilder } from '@components/FormBuilder';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import { fetchModel, updateModel } from '@store/slices/modelsSlice';

export const EditModel = () => {
  const navigate = useNavigate();
  const { username, modelId } = useParams();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.models);
  const [model, setModel] = useState<any>(null);
  const [templateValue, setTemplateValue] = useState<string>('');

  const form: any = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      type: '',
      category: '',
      template: '',
      description: '',
      fields: [],
    },

    validate: {
      name: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'nameIsRequired' });
        }
        return null;
      },
      type: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'typeIsRequired' });
        }
        return null;
      },
      category: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'categoryIsRequired' });
        }
        return null;
      },
    },
  });

  useEffect(() => {
    const loadModel = async () => {
      if (!username || !modelId) {
        return;
      }

      try {
        const result = await dispatch(
          fetchModel({ username, modelId })
        ).unwrap();

        setModel(result);

        const modelTemplate = result.template || '';
        setTemplateValue(modelTemplate);
        form.setValues({
          name: result.name,
          type: result.type,
          category: result.category,
          template: modelTemplate,
          description: result.description,
          fields: result.fields,
        });
      } catch (err: any) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message: err?.message || intl.formatMessage({ id: 'error' }),
          color: 'red',
        });
      }
    };

    loadModel();
  }, [dispatch, username, modelId, intl]);

  const handleSubmit = async (data: any) => {
    if (!username || !modelId) {
      return;
    }

    try {
      const result = await dispatch(
        updateModel({ username, modelId, modelData: data })
      ).unwrap();

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'updatedModel' },
          { name: result?.name }
        ),
        color: 'green',
      });

      navigate(routes.routes.models(username));
    } catch (err: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
        color: 'red',
      });
    }
  };

  const handleFieldsChange = useCallback(
    (fields: any[]) => {
      form.setFieldValue('fields', fields);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Handle template change
  const handleTemplateChange = (value: string) => {
    setTemplateValue(value);
    form.setFieldValue('template', value);
  };

  if (!model || isLoading) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl">
      <Title order={4} mb="md">
        {intl.formatMessage({ id: 'addModel' })}
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex gap="md" direction="column">
          <TextInput
            withAsterisk
            name="name"
            label={intl.formatMessage({ id: 'name' })}
            placeholder={intl.formatMessage({ id: 'name' })}
            key={form.key('name')}
            {...form.getInputProps('name')}
          />

          <Textarea
            minRows={3}
            name="description"
            label={intl.formatMessage({ id: 'description' })}
            placeholder={intl.formatMessage({ id: 'description' })}
            key={form.key('description')}
            {...form.getInputProps('description')}
          />

          <NativeSelect
            withAsterisk
            name="type"
            label={intl.formatMessage({ id: 'type' })}
            data={[
              {
                label: intl.formatMessage({ id: 'contract' }),
                value: 'CONTRACT',
              },
              { label: intl.formatMessage({ id: 'annex' }), value: 'ANNEX' },
            ]}
            {...form.getInputProps('type')}
          />

          <TextInput
            withAsterisk
            name="category"
            label={intl.formatMessage({ id: 'category' })}
            placeholder={intl.formatMessage({ id: 'category' })}
            key={form.key('category')}
            {...form.getInputProps('category')}
          />

          <FormBuilder
            fields={form.values?.fields || []}
            onFieldsChange={handleFieldsChange}
            // availableFieldTypes={[]}
          />

          <TemplateInfoAlert fields={form.values?.fields || []} />

          <ScriptTagsWarning template={templateValue} />

          <CodeEditor
            value={templateValue}
            onChange={handleTemplateChange}
            label={intl.formatMessage({ id: 'template' })}
            placeholder={intl.formatMessage({ id: 'template' })}
          />

          <Button fullWidth type="submit" variant="filled">
            {intl.formatMessage({ id: 'update' })}
          </Button>
        </Flex>
      </form>
    </Container>
  );
};
